import {
  formatDate,
  formatDateTime,
  formatCurrency,
  formatNumber,
  formatPercentFieldValue,
  formatCalculatedFieldForDisplay,
  normalizeCalculatedDisplayConfig,
} from "../../../utils/formatUtils";
import { isFieldValueMasked, MASKED_FIELD_DISPLAY } from "../../../utils/fieldConfigUtils";
import { sortListViewRecordRows } from "../../../utils/reportCurrencyAggregate";
import Barcode from "react-barcode";
import QRCode from "react-qr-code";

/**
 * Ensure display config is an object
 */
export const ensureDisplayConfig = (displayConfig) => {
  if (!displayConfig) return {};
  if (typeof displayConfig === 'string') {
    try {
      return JSON.parse(displayConfig);
    } catch (error) {
      console.warn('Failed to parse display_config:', error);
      return {};
    }
  }
  if (typeof displayConfig === 'object') {
    return displayConfig;
  }
  return {};
};

/**
 * Format field value for list view display
 */
export const formatFieldValue = (fieldName, value, dataType, displayConfig = null, fieldConfig = null) => {
  if (isFieldValueMasked({ type: dataType, config: fieldConfig }) && value) {
    return MASKED_FIELD_DISPLAY;
  }

  if (!value) return "";

  const parsedConfig = ensureDisplayConfig(displayConfig);
  const combinedConfig = {
    ...parsedConfig,
    ...(parsedConfig.calculated && typeof parsedConfig.calculated === 'object' ? parsedConfig.calculated : {})
  };

  if (dataType === 'date') {
    return formatDate(value);
  } else if (dataType === 'datetime') {
    return formatDateTime(value);
  } else if (dataType === 'rollupsummary') {
    const rollupConfig = parsedConfig.rollupSummary || {};
    const outputType = (rollupConfig.outputType || 'number').toLowerCase();

    if (outputType === 'currency') {
      const numValue = Number(value);
      if (!isNaN(numValue)) {
        return formatCurrency(numValue, 'INR');
      }
      return value;
    } else if (outputType === 'percent') {
      const numValue = Number(value);
      if (!isNaN(numValue)) {
        return formatPercentFieldValue(numValue, { scale: parsedConfig.scale });
      }
      return value;
    } else if (outputType === 'date') {
      return formatDate(value);
    } else if (outputType === 'datetime') {
      return formatDateTime(value);
    } else {
      return formatNumber(value);
    }
  } else if (dataType === 'percent') {
    const numValue = Number(value);
    if (!isNaN(numValue)) {
      return formatPercentFieldValue(numValue, combinedConfig);
    }
    return value;
  } else if (dataType === 'currency') {
    const numValue = Number(value);
    if (!isNaN(numValue)) {
      return formatCurrency(numValue, 'INR');
    }
    return value;
  } else if (dataType === 'number' || dataType === 'decimal' || dataType === 'integer') {
    return formatNumber(value);
  } else if (dataType === 'calculated') {
    return formatCalculatedFieldForDisplay(
      { value, displayConfig },
      null
    );
  } else if (dataType === 'barcode') {
    return (
      <Barcode
        value={String(value)}
        format={combinedConfig.format ?? 'CODE128'}
        width={combinedConfig.width ?? 2}
        height={combinedConfig.height ?? 80}
        displayValue={combinedConfig.displayValue ?? false}
      />
    );
  } else if (dataType === 'qrcode') {
    return (
      <QRCode
        value={String(value)}
        size={combinedConfig.size ?? 64}
        level={combinedConfig.errorCorrectionLevel ?? 'M'}
        style={{ maxWidth: '64px', height: 'auto' }}
      />
    );
  } else if (dataType === 'image') {
    const width = combinedConfig.width ?? 50;
    const height = combinedConfig.height ?? 50;
    return (
      <img
        src={value}
        alt="Thumbnail"
        style={{
          width: `${width}px`,
          height: `${height}px`,
          objectFit: combinedConfig.objectFit ?? 'cover',
          borderRadius: '4px',
          border: '1px solid #ddd'
        }}
        onError={(e) => {
          e.target.style.display = 'none';
          e.target.parentNode.innerHTML = '<span style="color: #999; font-size: 11px;">No image</span>';
        }}
      />
    );
  }

  return value;
};

const toDisplayPrimitive = (val) => {
  if (val == null || val === '') return null;
  if (typeof val === 'object' && !Array.isArray(val)) {
    const inner = val.value ?? val.displayValue ?? val.label ?? val.name;
    if (inner == null || inner === '') return null;
    if (typeof inner === 'object' && inner !== null && !Array.isArray(inner)) {
      return inner.value ?? inner.displayValue ?? inner.label ?? inner.name ?? null;
    }
    return inner;
  }
  return val;
};

/**
 * Plain-text label for list / list-detail rows (lookup owner, references, picklists, etc.).
 * Prefers displayValue like ResizableTable — not raw UUIDs.
 */
export const getListViewFieldDisplayText = (field, fieldName, fieldDataTypes = {}) => {
  if (field == null) return '';

  if (typeof field === 'object' && !Array.isArray(field)) {
    const displayPrimitive = toDisplayPrimitive(field.displayValue);
    if (displayPrimitive != null && displayPrimitive !== '') {
      return String(displayPrimitive);
    }

    const rawValue = field.value !== undefined ? field.value : field;
    if (rawValue == null || rawValue === '') return '';

    const dataType = fieldDataTypes[fieldName] || field.type || '';
    const fieldConfig = field.config ?? field.fieldConfig ?? null;
    if (isFieldValueMasked({ ...field, type: dataType, config: fieldConfig })) {
      if (rawValue != null && rawValue !== '') {
        return MASKED_FIELD_DISPLAY;
      }
    }

    const formatted = formatFieldValue(fieldName, rawValue, dataType, field.displayConfig, fieldConfig);
    if (typeof formatted === 'string' || typeof formatted === 'number') {
      return String(formatted);
    }
    if (typeof rawValue === 'object') {
      const nested = toDisplayPrimitive(rawValue);
      if (nested != null && nested !== '') return String(nested);
    }
    return String(rawValue);
  }

  return String(field);
};

/**
 * Prepare table data for ResizableTable
 */
export const prepareTableData = (records, objectName, fieldDataTypes, fieldConfigMap = {}) => {
  const rows = records || [];
  return rows.map((row) => {
    const tableRecord = { ...row };
    tableRecord.objectName = objectName;
    const recordIdValue =
      (row?.id && typeof row.id === 'object' ? row.id.value : row?.id) ??
      row?.Id ??
      row?.ID ??
      null;
    tableRecord.__recordId = recordIdValue;

    if (tableRecord.id && typeof tableRecord.id === 'object') {
      tableRecord.id.type = tableRecord.id.type || 'uuid';
      tableRecord.id.referenceObject = tableRecord.id.referenceObject || objectName;
    }

    Object.keys(tableRecord).forEach((key) => {
      if (key === 'id' || key === 'objectName') return;
      const field = tableRecord[key];
      const dataType = fieldDataTypes?.[key];
      if (field && typeof field === 'object') {
        if (key === 'related_object' && field.objectLabel) {
          tableRecord[key] = {
            ...field,
            displayValue: field.objectLabel
          };
        } else {
          const rawValue = field.value !== undefined ? field.value : field;
          const fieldType = (field.type || dataType || '').toLowerCase();
          const fieldConfig = field.config ?? field.fieldConfig ?? fieldConfigMap?.[key] ?? null;

          if (isFieldValueMasked({ ...field, type: fieldType, config: fieldConfig })) {
            const hasValue = rawValue != null && rawValue !== '';
            tableRecord[key] = {
              ...field,
              type: fieldType || dataType,
              displayValue: hasValue ? MASKED_FIELD_DISPLAY : field.displayValue,
              isMasked: hasValue,
            };
            return;
          }

          if (fieldType === 'calculated') {
            const normalizedConfig = normalizeCalculatedDisplayConfig(field.displayConfig);
            tableRecord[key] = {
              ...field,
              type: 'calculated',
              calculatedReturnType: normalizedConfig.returnType,
              calculatedConfig: normalizedConfig,
              displayValue: formatCalculatedFieldForDisplay(field, tableRecord),
            };
            return;
          }

          if (dataType === 'date') {
            tableRecord[key] = {
              ...field,
              type: 'date',
              displayValue: rawValue ? formatDate(rawValue) : field.displayValue
            };
          } else if (dataType === 'datetime') {
            tableRecord[key] = {
              ...field,
              type: 'datetime',
              displayValue: rawValue ? formatDateTime(rawValue) : field.displayValue
            };
          } else if (dataType || fieldType) {
            tableRecord[key] = {
              ...field,
              type: fieldType || dataType,
            };
          }
        }
      }
    });
    return tableRecord;
  });
};

/**
 * Get filtered and sorted records
 */
export const getFilteredAndSortedRecords = (
  records,
  searchTerm,
  filters,
  sortBy,
  sortOrder,
  fieldDataTypes = {}
) => {
  let filtered = records.filter((row) => {
    if (searchTerm && searchTerm.trim() !== "") {
      const searchLower = searchTerm.toLowerCase();
      const searchMatch = Object.keys(row).some(key => {
        if (key === 'id') return false;

        const field = row[key];
        if (field?.isReference) {
          const fieldValue = field.value?.toString().toLowerCase() || "";
          const displayValue = field.displayValue?.toString().toLowerCase() || "";
          return fieldValue.includes(searchLower) || displayValue.includes(searchLower);
        } else {
          const fieldValue = field?.value?.toString().toLowerCase() || "";
          return fieldValue.includes(searchLower);
        }
      });

      if (!searchMatch) return false;
    }

    return Object.entries(filters).every(([key, value]) => {
      if (!value) return true;

      if (row[key]?.isReference) {
        const fieldValue = row[key]?.value?.toString().toLowerCase() || "";
        const displayValue = row[key]?.displayValue?.toString().toLowerCase() || "";
        return fieldValue.includes(value.toLowerCase()) || displayValue.includes(value.toLowerCase());
      }

      const fieldValue = row[key]?.value?.toString().toLowerCase() || "";
      return fieldValue.includes(value.toLowerCase());
    });
  });

  if (sortBy && sortOrder) {
    return sortListViewRecordRows(filtered, sortBy, sortOrder, fieldDataTypes);
  }
  return filtered;
};
